import { useCallback, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ConfirmDialog } from './ConfirmDialog';
import { useCouplingStore } from '../../store/couplingStore';
import type { ZoneCoupling } from '../../types/coupling';

/**
 * One confirmation flow for switching a Trennstelle, shared by the map marker popup, the KVS
 * busbar view and the ONS details (doc/Architektur/NETZVERBUND.md 6). Wording as in the
 * mobile app: "Möchten Sie die Trennstelle … wirklich öffnen/schließen?".
 *
 * Usage: `const { requestSwitch, dialog } = useCouplingSwitch();` — call `requestSwitch(c, target)`
 * from a button and render `{dialog}` once in the component tree.
 */
export function useCouplingSwitch(): { requestSwitch: (coupling: ZoneCoupling, target: boolean) => void; dialog: ReactNode } {
  const setState = useCouplingStore(s => s.setState);
  const [pending, setPending] = useState<{ coupling: ZoneCoupling; target: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestSwitch = useCallback((coupling: ZoneCoupling, target: boolean) => {
    setError(null);
    setPending({ coupling, target });
  }, []);

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await setState(pending.coupling.id, pending.target);
      setPending(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Schalten fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const dialog = pending
    ? createPortal(
        <ConfirmDialog
          tone="danger"
          title={pending.target ? 'Trennstelle schließen' : 'Trennstelle öffnen'}
          message={
            `Möchten Sie die Trennstelle „${pending.coupling.name}“ wirklich ${pending.target ? 'schließen' : 'öffnen'}? ` +
            (pending.target
              ? `Die Zonen ${pending.coupling.zoneAName ?? pending.coupling.zoneAId} und ${pending.coupling.zoneBName ?? pending.coupling.zoneBId} werden danach als ein Netz gerechnet.`
              : `Die Zonen ${pending.coupling.zoneAName ?? pending.coupling.zoneAId} und ${pending.coupling.zoneBName ?? pending.coupling.zoneBId} werden danach getrennt gerechnet.`)
          }
          confirmLabel={pending.target ? 'Schließen' : 'Öffnen'}
          onConfirm={() => void confirm()}
          onCancel={() => { setPending(null); setError(null); }}
          disabled={busy}
          error={error}
        />,
        document.body,
      )
    : null;

  return { requestSwitch, dialog };
}

export const COUPLING_CLOSED_COLOR = '#16a34a';
export const COUPLING_OPEN_COLOR = '#f97316';
